import { Reducer } from 'redux';

import { QuestionSummary } from '~src/modules/reports/interfaces/questions.interface';
import {
  ADD_BREADCRUMB_LINK,
  CHANGE_ANSWERS_SUMMARY,
  CHANGE_ENTITY_LOADING,
  CHANGE_GRAPH_VISIBILITY,
  CHANGE_HEADER_LOADING,
  CHANGE_HEADER_TAB_INDEX,
  CHANGE_PERIOD_SELECTOR_VISIBILITY,
  CHANGE_QUESTIONS_SUMMARY,
  CHANGE_RATING,
  CHANGE_RATING_VISIBILITY,
  CHANGE_SELECTED_PERIOD,
  OVERWRITE_BREADCRUMB_LINKS,
  SET_HEADER_INFORMATION,
  SUMMARY_FAILED,
  SUMMARY_REQUEST,
} from '~src/modules/reports/reportsHeader/constans/action.types';
import { calcIndex } from '~src/modules/reports/services/ratingTransformations';
import {
  AnswerSummary,
  BreadCrumb,
  HeaderInformation,
  HeaderRating,
} from '~src/modules/reports/interfaces/header.interface';
import { ThunkFailAction, ThunkSuccessAction } from '~src/services/apiMiddleware';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import {
  GET_USER_ANSWERS_SUCCESS,
  GET_USER_NEWS_SUCCESS,
  GET_USER_QUESTIONS_SUCCESS,
  GET_USER_TOPICS_SUCCESS,
  GetDirectFeedback,
  GetUserSurveys,
  USER_PROFILE_ANY_REQUEST,
} from '~src/modules/reports/userProfile/constants/actions';
import { GetSubjectNotes } from '~src/modules/reports/shared/components/notes/constants/actions';
import {
  GET_GROUP_ANSWERS_SUCCESS,
  GET_GROUP_NEWS_SUCCESS,
  GET_GROUP_QUESTIONS_SUCCESS,
  GET_GROUP_TOPICS_SUCCESS,
  GROUP_PROFILE_ANY_REQUEST,
} from '~src/modules/reports/groupProfile/constants/actions';
import {
  COMPANY_PROFILE_ANY_REQUEST,
  GET_COMPANY_ANSWERS_SUCCESS,
  GET_COMPANY_NEWS_SUCCESS,
  GET_COMPANY_QUESTIONS_SUCCESS,
  GET_COMPANY_TOPICS_SUCCESS,
} from '~src/modules/reports/companyProfile/constants/companyProfileActions';
import {
  GET_SURVEY_ANSWERS_SUCCESS,
  GET_SURVEY_NEWS_SUCCESS,
  GET_SURVEY_QUESTIONS_SUCCESS,
  GET_SURVEY_TOPICS_SUCCESS,
  SURVEY_PROFILE_ANY_REQUEST,
} from '~src/modules/reports/surveyProfile/actions/surveyProfile.actions';
import {
  ChangeHeaderTabIndexAction,
  HeaderInformationActionI
} from "~src/modules/reports/reportsHeader/actions/headerActions.interface";

export interface HeaderState {
  headerInformation: HeaderInformation | null;
  showRatingOnHeader: boolean;
  headerRating: HeaderRating | null;
  headerLoading: boolean;
  entityLoading: boolean;
  summaries: {
    loading: boolean;
    question: QuestionSummary | null;
    answer: AnswerSummary | null;
  };
  showPeriodSelector: boolean;
  showGraph: boolean;
  selectedPeriod: PeriodValues;
  canExport: boolean;
}

export function getInitialHeaderState(): HeaderState {
  return {
    headerInformation: null,
    showRatingOnHeader: false,
    headerRating: null,
    headerLoading: false,
    entityLoading: false,
    summaries: {
      loading: false,
      question: null,
      answer: null,
    },
    showPeriodSelector: false,
    showGraph: false,
    selectedPeriod: PeriodValues.AllTime,
    canExport: false,
  };
}

function changeHeaderRating(rating: HeaderRating) {
  const tmpRating = rating;
  if (tmpRating.result) {
    tmpRating.ratingIndex = calcIndex(tmpRating.result.avg_rating_ord, tmpRating.scale);
  }
  return tmpRating;
}

function addBreadCrumbLinks(headerInformation: HeaderInformation | null, link: BreadCrumb) {
  if (headerInformation) {
    const tmpLinks = headerInformation.breadCrumbLinks;
    tmpLinks.push(link);
    return tmpLinks;
  }
  return null;
}

const headerReducer: Reducer<HeaderState, ThunkSuccessAction | ThunkFailAction> = (
  state: HeaderState = getInitialHeaderState(),
  action: HeaderInformationActionI|ChangeHeaderTabIndexAction,
) => {
  switch (action.type) {
    case SET_HEADER_INFORMATION: {
      return {
        ...state,
        headerInformation: {
          ...state.headerInformation,
          ...action.headerInformation,
        },
      };
    }
    case CHANGE_HEADER_TAB_INDEX: {
      return {
        ...state,
        headerInformation: {
          ...state.headerInformation,
          selectedTabIndex: action.idx,
        },
      };
    }
    case ADD_BREADCRUMB_LINK: {
      return {
        ...state,
        headerInformation: {
          ...state.headerInformation,
          breadCrumbLinks: addBreadCrumbLinks(state.headerInformation, action.link),
        },
      };
    }
    case OVERWRITE_BREADCRUMB_LINKS: {
      return {
        ...state,
        headerInformation: {
          ...state.headerInformation,
          breadCrumbLinks: action.links,
        },
      };
    }
    case CHANGE_RATING_VISIBILITY: {
      return { ...state, showRatingOnHeader: action.showRatingOnHeader };
    }
    case CHANGE_RATING: {
      return { ...state, headerRating: changeHeaderRating(action.rating) };
    }
    case CHANGE_HEADER_LOADING: {
      return { ...state, headerLoading: action.loading };
    }
    case CHANGE_ENTITY_LOADING: {
      return { ...state, entityLoading: action.loading };
    }
    case SUMMARY_REQUEST: {
      return { ...state, summaries: { ...state.summaries, loading: true } };
    }
    case CHANGE_QUESTIONS_SUMMARY: {
      return {
        ...state,
        summaries: {
          ...state.summaries,
          loading: false,
          question: action.response.data,
        },
      };
    }
    case CHANGE_ANSWERS_SUMMARY: {
      return {
        ...state,
        summaries: {
          ...state.summaries,
          loading: false,
          answer: action.response.data,
        },
      };
    }
    case SUMMARY_FAILED: {
      return { ...state, summaries: { ...state.summaries, loading: false } };
    }

    case CHANGE_GRAPH_VISIBILITY:
      return { ...state, showGraph: action.showGraph };

    case CHANGE_PERIOD_SELECTOR_VISIBILITY:
      return { ...state, showPeriodSelector: action.showPeriodSelector };
    case CHANGE_SELECTED_PERIOD:
      return { ...state, selectedPeriod: action.selectedPeriod };
    case USER_PROFILE_ANY_REQUEST:
    case GetDirectFeedback.Request:
    case GetUserSurveys.Request:
    case GetSubjectNotes.Request:
    case GROUP_PROFILE_ANY_REQUEST:
    case COMPANY_PROFILE_ANY_REQUEST:
    case SURVEY_PROFILE_ANY_REQUEST:
      return { ...state, canExport: false };
    case GET_USER_TOPICS_SUCCESS:
    case GET_USER_QUESTIONS_SUCCESS:
    case GET_USER_ANSWERS_SUCCESS:
    case GET_USER_NEWS_SUCCESS:
    case GET_GROUP_TOPICS_SUCCESS:
    case GET_GROUP_QUESTIONS_SUCCESS:
    case GET_GROUP_ANSWERS_SUCCESS:
    case GET_GROUP_NEWS_SUCCESS:
    case GET_COMPANY_TOPICS_SUCCESS:
    case GET_COMPANY_QUESTIONS_SUCCESS:
    case GET_COMPANY_ANSWERS_SUCCESS:
    case GET_COMPANY_NEWS_SUCCESS:
    case GET_SURVEY_NEWS_SUCCESS:
    case GET_SURVEY_TOPICS_SUCCESS:
    case GET_SURVEY_QUESTIONS_SUCCESS:
    case GET_SURVEY_ANSWERS_SUCCESS:
      return { ...state, canExport: true };
    default:
      return { ...state };
  }
};

export default headerReducer;
